import React, { useState, useEffect } from "react";
import { Search, Filter, Plus, MoreHorizontal, Edit, Trash2, ArrowLeft, Calendar } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { ApiResponse } from "../types/common";
import { couponRelatedApi } from "../utils/api";

interface PromotionalCoupon {
  id: string;
  name: string;
  couponId: string;
  description: string;
  starts_at: string;
  expires_at: string;
  usage_limit: number;
  limit_used: number;
  userType: "COACH" | "ATHLETE" | "AGENT" | "ALL";
  active: boolean;
  createdAt: string;
}

export function PromotionalCoupons() {
  const [coupons, setCoupons] = useState<PromotionalCoupon[]>([]);
  const [filteredCoupons, setFilteredCoupons] = useState<PromotionalCoupon[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState<"list" | "create" | "edit">("list");
  const [editingCoupon, setEditingCoupon] = useState<PromotionalCoupon | null>(null);

  // Filter states
  const [searchName, setSearchName] = useState("");
  const [searchcouponId, setSearchcouponId] = useState("");
  const [selectedUserType, setSelectedUserType] = useState<string>("all");
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [minusage_limit, setMinusage_limit] = useState("");
  const [maxusage_limit, setMaxusage_limit] = useState("");

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    couponId: "",
    description: "",
    starts_at: "",
    expires_at: "",
    usage_limit: "",
    userType: ""
  });

  // Simulate API call to fetch coupons
  useEffect(() => {
    const fetchCoupons = async () => {
      setIsLoading(true);
      try {
        const response: ApiResponse = await couponRelatedApi.getAllCoupons();
        
        setCoupons(response.data.promotions || []);
        setFilteredCoupons(response.data.promotions || []);
      } catch (error) {
        console.error("Failed to fetch coupons:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (currentView === "list") {
      fetchCoupons();
    }
  }, [currentView]);

  // Apply filters
  useEffect(() => {
    let filtered = [...coupons];

    // Name search
    if (searchName) {
      filtered = filtered.filter(coupon => 
        coupon.name.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    // Coupon code search
    if (searchcouponId) {
      filtered = filtered.filter(coupon => 
        coupon.couponId.toLowerCase().includes(searchcouponId.toLowerCase())
      );
    }

    // User type filter
    if (selectedUserType !== "all") {
      filtered = filtered.filter(coupon => coupon.userType === selectedUserType);
    }

    // Status filter
    if (selectedStatus !== "all") {
      const now = new Date();
      if (selectedStatus === "active") {
        filtered = filtered.filter(coupon => new Date(coupon.expires_at) >= now && new Date(coupon.starts_at) <= now);
      } else if (selectedStatus === "expired") {
        filtered = filtered.filter(coupon => new Date(coupon.expires_at) <= now);
      } else if (selectedStatus === "disabled") {
        filtered = filtered.filter(coupon => new Date(coupon.starts_at) >= now);
      }
    }

    // Usage limit filter
    if (minusage_limit) {
      filtered = filtered.filter(coupon => coupon.usage_limit >= parseInt(minusage_limit));
    }
    if (maxusage_limit) {
      filtered = filtered.filter(coupon => coupon.usage_limit <= parseInt(maxusage_limit));
    }

    setFilteredCoupons(filtered);
  }, [coupons, searchName, searchcouponId, selectedUserType, selectedStatus, minusage_limit, maxusage_limit]);

  const clearFilters = () => {
    setSearchName("");
    setSearchcouponId("");
    setSelectedUserType("all");
    setSelectedStatus("all");
    setMinusage_limit("");
    setMaxusage_limit("");
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Simulate API call to create/update coupon
      // const response = await fetch(`host/api/v1/promotional-coupons${editingCoupon ? `/${editingCoupon.id}` : ''}`, {
      //   method: editingCoupon ? 'PUT' : 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${localStorage.getItem("authToken")}`
      //   },
      //   body: JSON.stringify(formData)
      // });

      // Mock success
      console.log(editingCoupon ? "Updating coupon:" : "Creating coupon:", formData);
      
      // Reset form and go back to list
      setFormData({
        name: "",
        couponId: "",
        description: "",
        starts_at: "",
        expires_at: "",
        usage_limit: "",
        userType: ""
      });
      setEditingCoupon(null);
      setCurrentView("list");
    } catch (error) {
      console.error("Failed to save coupon:", error);
    }
  };

  const handleEdit = (coupon: PromotionalCoupon) => {
    setEditingCoupon(coupon);
    setFormData({
      name: coupon.name,
      couponId: coupon.couponId,
      description: coupon.description,
      starts_at: coupon.starts_at,
      expires_at: coupon.expires_at,
      usage_limit: coupon.usage_limit.toString(),
      userType: coupon.userType
    });
    setCurrentView("edit");
  };

  const handleDelete = async (id: string) => {
    try {
      // Simulate API call to delete coupon
      // await fetch(`host/api/v1/promotional-coupons/${id}`, {
      //   method: 'DELETE',
      //   headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
      // });

      // Mock deletion
      setCoupons(prev => prev.filter(coupon => coupon.id !== id));
      console.log("Deleted coupon:", id);
    } catch (error) {
      console.error("Failed to delete coupon:", error);
    }
  };

  const getCouponStatus = (coupon: PromotionalCoupon) => {
    // if (!coupon.active) return { status: "Disabled", variant: "secondary" as const };
    
    const now = new Date();
    const expires_at = new Date(coupon.expires_at);
    const starts_at = new Date(coupon.starts_at);
    if (now <= expires_at && now >= starts_at) return { status: "Active", variant: "default" as const };
    if (now > expires_at) return { status: "Expired", variant: "destructive" as const };
    if (now < starts_at) return { status: "Scheduled", variant: "outline" as const };
    if (coupon.limit_used >= coupon.usage_limit) return { status: "Used Up", variant: "secondary" as const };
    
    return { status: "Active", variant: "default" as const };
  };

  if (currentView === "create" || currentView === "edit") {
    return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setCurrentView("list");
              setEditingCoupon(null);
              setFormData({
                name: "",
                couponId: "",
                description: "",
                starts_at: "",
                expires_at: "",
                usage_limit: "",
                userType: ""
              });
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          <div>
            <h1>Create new</h1>
            <p className="text-muted-foreground">
              {currentView === "edit" ? "Update coupon details" : "Add a new promotional coupon"}
            </p>
          </div>
        </div>

        {/* Create/Edit Form */}
        <Card>
          <CardHeader>
            <CardTitle>{currentView === "edit" ? "Edit Coupon" : "Create new"}</CardTitle>
            <CardDescription>
              Fill in the details for the promotional coupon
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter coupon name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="couponId">Coupon code</Label>
                <Input
                  id="couponId"
                  value={formData.couponId}
                  onChange={(e) => setFormData(prev => ({ ...prev, couponId: e.target.value }))}
                  placeholder="Enter coupon code"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="starts_at">Starts_at</Label>
                  <div className="relative">
                    <Input
                      id="starts_at"
                      type="date"
                      value={formData.starts_at}
                      onChange={(e) => setFormData(prev => ({ ...prev, starts_at: e.target.value }))}
                      required
                    />
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="expires_at">Expires_at</Label>
                  <div className="relative">
                    <Input
                      id="expires_at"
                      type="date"
                      value={formData.expires_at}
                      onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
                      required
                    />
                    <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="usage_limit">Usage Limit</Label>
                <Input
                  id="usage_limit"
                  type="number"
                  min="1"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData(prev => ({ ...prev, usage_limit: e.target.value }))}
                  placeholder="Enter usage limit"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="userType">User Type</Label>
                <Select 
                  value={formData.userType} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, userType: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select User Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Users</SelectItem>
                    <SelectItem value="COACH">Coach</SelectItem>
                    <SelectItem value="ATHLETE">Athlete</SelectItem>
                    <SelectItem value="AGENT">Agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-4 pt-4">
                <Button type="submit">
                  Save
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => {
                    setCurrentView("list");
                    setEditingCoupon(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
          <div className="space-y-2">
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1>Promotional Coupons</h1>
          <p className="text-muted-foreground">
            Manage promotional coupons and discount codes
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
          <Button onClick={() => setCurrentView("create")}>
            <Plus className="h-4 w-4 mr-2" />
            Create new
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Coupon List</CardTitle>
              <CardDescription>
                Filter and search through all promotional coupons
              </CardDescription>
            </div>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <div>
              <label className="text-sm mb-2 block">Coupon Name</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search name..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="text-sm mb-2 block">Coupon Code</label>
              <Input
                placeholder="Search code..."
                value={searchcouponId}
                onChange={(e) => setSearchcouponId(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm mb-2 block">User Type</label>
              <Select value={selectedUserType} onValueChange={setSelectedUserType}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="ALL">All Users</SelectItem>
                  <SelectItem value="COACH">Coach</SelectItem>
                  <SelectItem value="ATHLETE">Athlete</SelectItem>
                  <SelectItem value="AGENT">Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm mb-2 block">Status</label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm mb-2 block">Min Usage Limit</label>
              <Input
                type="number"
                placeholder="Min limit"
                value={minusage_limit}
                onChange={(e) => setMinusage_limit(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm mb-2 block">Max Usage Limit</label>
              <Input
                type="number"
                placeholder="Max limit"
                value={maxusage_limit}
                onChange={(e) => setMaxusage_limit(e.target.value)}
              />
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredCoupons.length} of {coupons.length} coupons
            </p>
          </div>

          {/* Coupons Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Coupon Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Expires At</TableHead>
                  <TableHead>Limit Used</TableHead>
                  <TableHead>Usage Limit</TableHead>
                  <TableHead>User Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[50px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCoupons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      <div className="text-muted-foreground">
                        <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No coupons found matching your filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCoupons.map((coupon) => {
                    const status = getCouponStatus(coupon);
                    return (
                      <TableRow key={coupon.id}>
                        <TableCell>{coupon.name}</TableCell>
                        <TableCell className="font-mono text-sm">{coupon.couponId}</TableCell>
                        <TableCell>
                          <div className="max-w-[200px] truncate" title={coupon.description}>
                            {coupon.description}
                          </div>
                        </TableCell>
                        <TableCell>{coupon.expires_at}</TableCell>
                        <TableCell>{coupon.limit_used}</TableCell>
                        <TableCell>{coupon.usage_limit}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              coupon.userType === "ALL" ? "default" : 
                              coupon.userType === "COACH" ? "secondary" : 
                              coupon.userType === "ATHLETE" ? "outline" : "destructive"
                            }
                          >
                            {coupon.userType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant}>
                            {status.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(coupon)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit Coupon
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                View Usage History
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDelete(coupon.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Coupon
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}